import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../../Assets/Styles/pages/header.scss";
import "../../Assets/Styles/pages/uploadImage.scss";
import "../../Assets/Styles/components/selectTemplate.scss";
import CancelButton from "../CancelButton/Cancel_button";
import { CircularProgress } from "@mui/material";
import {
  setID,
  setBatchImages,
  setCreate_Template,
  setTemplateAuthoringAspectPreset,
  defaultCreateTemplateTilePattern1,
} from "../../Redux/UserSlice/userSlice";
import type { CreateTemplateTilePattern1Draft } from "../../Redux/UserSlice/userSlice";
import { GetTemplateData } from "../../actions/Template/Template";
import {
  clearBatchUrls,
  setTemplateForImage,
  setImageUrl,
  setBatchTileForAll,
} from "../../Redux/MainImage/imageSlice";
import { setExtension } from "../../Redux/DownloadSlice/downloadSlice";
import { deleteAll } from "../../Redux/WaterMarks/watermarks";
import { TEMPLATE_DEMO_BACKGROUND_URL } from "../../Config/templateAspectRatios";
import NoTemplates from "../NoTemplates/noTemplates";
import helper_functions from "../../Config/helper_functions";
import { RootState } from "../../Redux/store";
import { usePremiumStatus } from "../../hooks/usePremiumStatus";
import PremiumGateDialog from "../Common/PremiumGateDialog";

interface TemplateItem {
  id: string;
  name: string;
  date?: number;
  media?: { thumbUrl?: string; originalUrl?: string; url?: string; largeUrl?: string } | null;
  thumbUrl?: string;
  thumbnailUrl?: string;
  originalUrl?: string;
  url?: string;
}

interface SelectTemplateProps {
  open: boolean;
  setOpen: (v: boolean) => void;
  portraitTemp: TemplateItem | null;
  setPortrait: (v: TemplateItem | null) => void;
  landcsapeTemp: TemplateItem | null;
  setLandcsape: (v: TemplateItem | null) => void;
  /** Called once an existing template is confirmed (Apply) -- photos are already uploaded at
   * this point, so this just navigates into the editor instead of opening an image picker. */
  onNext: () => void;
  /** "Create new template" needs its own destination: the real Create Template page, not the
   * batch photos' editor -- a brand new template isn't applied to the uploaded batch at all. */
  onCreateNew: () => void;
  setMultiple: (v: boolean) => void;
  /** Number of uploaded batch photos this template will be applied to (Batch2 reference
   * copy: "...to apply to N photos" / "Apply to N Photos"). */
  photoCount: number;
}

const SelectTemplate = ({
  open,
  setOpen,
  portraitTemp,
  setPortrait,
  landcsapeTemp,
  setLandcsape,
  onNext,
  onCreateNew,
  setMultiple,
  photoCount,
}: SelectTemplateProps) => {
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGateDialog, setShowGateDialog] = useState(false);
  const [applying, setApplying] = useState(false);
  // EWW-443: bumped on every Apply and on Cancel, so a still-in-flight apply from a template
  // the user backed out of can recognize it's stale (after its awaits resolve) and bail out
  // instead of clobbering a later, correct selection.
  const applyRequestIdRef = useRef(0);
  const dispatch = useDispatch();
  const isPremium = usePremiumStatus();
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  useEffect(() => {
    if (
      Object.keys(userSliceState.user_Auth).length &&
      Object.keys(userSliceState.template).length
    )
      setTemplates((userSliceState.template as unknown) as TemplateItem[]);
  }, [userSliceState]);

  const handleLoad = () => {
    setLoading(false);
  };
  const handleTemplateNext = async () => {
    setMultiple(true);
    if (landcsapeTemp || portraitTemp) {
      const requestId = ++applyRequestIdRef.current;
      const landId = landcsapeTemp?.id;
      const portId = portraitTemp?.id;
      const sameTemplate = landId && portId && landId === portId;
      // Always clear any leftover watermark items from a previous session before applying --
      // matches templates.tsx's real single-image apply flow, which does this unconditionally.
      dispatch(deleteAll());
      setApplying(true);

      // GetTemplateData fetches from the server and only *asynchronously* caches the
      // template's watermark data (setTemplateBatch) -- previously this fired without
      // awaiting it before navigating to /edit. The batch's first photo is already the
      // "current" image the instant /edit mounts, so its canvas tried to resolve the template
      // before the fetch resolved, fell back to a raw "template"-property placeholder item
      // (since the cache lookup missed), and clicking it opened the Templates panel instead
      // of the real watermark's properties. Every other photo was fine because by the time
      // you clicked through to them, the fetch had long since finished in the background.
      // Awaiting it here ensures the cache is populated *before* /edit ever mounts.
      let tileDraftToApply: CreateTemplateTilePattern1Draft | undefined;

      if (landcsapeTemp) {
        const result = (await dispatch(
          GetTemplateData(
            landcsapeTemp.id,
            [],
            false,
            false,
            userSliceState.canvasSize?.width ?? 651
          )
        )) as any;
        if (result && result.tileDraft) {
          tileDraftToApply = result.tileDraft;
        }
      }
      if (!sameTemplate && portraitTemp) {
        const result = (await dispatch(
          GetTemplateData(
            portraitTemp.id,
            [],
            false,
            false,
            userSliceState.canvasSize?.width ?? 651
          )
        )) as any;
        if (result && result.tileDraft && !tileDraftToApply) {
          tileDraftToApply = result.tileDraft;
        }
      }

      // Bail out if the user cancelled (or applied a different template) while the awaits
      // above were in flight -- otherwise this stale run would still commit its own (now
      // wrong) template selection and navigate, clobbering whatever the user actually chose.
      if (applyRequestIdRef.current !== requestId) return;

      dispatch(
        setID({
          landcsapeTemp: landcsapeTemp?.id,
          portraitTemp: portraitTemp?.id,
        })
      );
      dispatch(setBatchImages(true));
      // Stamp every already-uploaded batch photo with the just-chosen template --
      // canvasUtils.ts resolves each photo's template via
      // `url?.template || url?.landcsapeTemp || url?.portraitTemp`, fields that were set
      // at upload time from whatever `current_TemplateID` happened to be then (often a
      // stale value from an earlier session, since photos are now uploaded *before* a
      // template is picked). Without this, that stale value kept winning over the new pick.
      dispatch(
        setTemplateForImage({
          index: -1,
          tempID: (landcsapeTemp ?? portraitTemp)?.id ?? null,
        })
      );
      dispatch(setBatchTileForAll(tileDraftToApply ?? defaultCreateTemplateTilePattern1));
      setApplying(false);
      setOpen(false);
      onNext();
    }
  };
  // EWW-443: shared by every way out of this modal (backdrop/Esc, the X, and the footer Cancel
  // button) -- invalidates `applyRequestIdRef` so an Apply still in flight at this moment can't
  // land after the fact.
  const handleCancel = () => {
    applyRequestIdRef.current += 1;
    setApplying(false);
    setLandcsape(null);
    setPortrait(null);
    setOpen(false);
  };
  const handleTemplateCreate = () => {
    setMultiple(false);
    setLandcsape(null);
    setPortrait(null);
    setOpen(false);

    // Same "start with sample background" flow as /addtemplate's own create-new tile
    // (UploadSingleImageButton.tsx's handleStartWithSampleBackground) -- a brand new template
    // isn't applied to the uploaded batch photos, so it gets its own fresh single-image
    // session instead of reusing them.
    dispatch(setExtension("png"));
    dispatch(
      setImageUrl({
        url: TEMPLATE_DEMO_BACKGROUND_URL,
        name: "canvasBgDemo.png",
        metadata: JSON.stringify({}),
      })
    );
    dispatch(setCreate_Template(true));
    dispatch(setTemplateAuthoringAspectPreset("1:1"));
    dispatch(deleteAll());
    dispatch(setID(null));
    dispatch(clearBatchUrls());
    onCreateNew();
  };
  return (
    <>
      <Modal
        className="select-template-modal"
        open={open}
        onClose={handleCancel}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Fade in={open}>
          <div className="st-modal-shell noScroller">
            <CancelButton close={handleCancel} />
            <div className="st-header">
              <div className="st-header__title RalewayBold text_22 black_0C0C0C">
                Choose a watermark template
              </div>
              <div className="st-header__subtitle Aktiv Grotesk Corp text_15">
                Pick from your saved templates or create a new one to apply to{" "}
                {photoCount} photos.
              </div>
            </div>
            {templates.length ? (
              <div className="select-template-row st-grid-wrapper">
                <div className="st-grid noScroller select-template-scroll-container">
                  {/* "Create new template" lives only in the empty state below now -- once the
                      user has templates, this modal is about picking one of them, and Create is
                      still reachable from /addtemplate's own create tile. */}
                  {templates?.map((template, index) => {
                    const isLocked = !isPremium && index > 0;
                    const isSelected = Boolean(
                      portraitTemp && portraitTemp.id === template.id
                    );
                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => {
                          if (isLocked) {
                            setShowGateDialog(true);
                            return;
                          }
                          // Single-step selection:
                          // selecting any template should apply for both portrait + landscape,
                          // so user doesn't have to pick separately.
                          if (isSelected) {
                            setPortrait(null);
                            setLandcsape(null);
                          } else {
                            setPortrait(template);
                            setLandcsape(template);
                          }
                        }}
                        className={`st-card ${isSelected ? "st-card--selected" : ""}`}
                      >
                        {isLocked && (
                          <span className="st-card__pro-badge">Pro</span>
                        )}
                        <div className="st-card__thumb">
                          {loading && (
                            <CircularProgress size={22} className="st-card__spinner" />
                          )}
                          <img
                            className={`select-template-thumbnail ${isLocked ? "locked-template-thumbnail" : ""}`}
                            onLoad={handleLoad}
                            onError={handleLoad}
                            alt={template?.name || "Template"}
                            src={
                              template?.media?.thumbUrl ||
                              template?.media?.url ||
                              template?.media?.originalUrl ||
                              template?.thumbUrl ||
                              template?.thumbnailUrl ||
                              ""
                            }
                          />
                          {isLocked && (
                            <div className="template-lock-overlay st-card__lock">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="white"
                                aria-hidden="true"
                              >
                                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="st-card__meta">
                          <div className="st-card__meta-text">
                            <span className="st-card__name">{template.name}</span>
                            <span className="st-card__date">
                              {template.date
                                ? helper_functions.unixTimestampToDateTime(
                                  template.date
                                )
                                : ""}
                            </span>
                          </div>
                          {isSelected && (
                            <span className="st-card__check">✓</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="select-template-row st-grid-wrapper d-flex flex-column align-items-center">
                <NoTemplates list={true} />
                <button
                  type="button"
                  onClick={handleTemplateCreate}
                  className="st-card st-card--create st-card--create-empty"
                >
                  <span className="st-card--create__icon">+</span>
                  <span className="st-card--create__label">Create new template</span>
                  <span className="st-card--create__sublabel">
                    Design text or upload a logo
                  </span>
                </button>
              </div>
            )}
            <div className="st-footer productSansLight text_13">
              <span className="st-footer__status">
                {portraitTemp || landcsapeTemp ? (
                  <>
                    Using{" "}
                    <strong>{(portraitTemp ?? landcsapeTemp)?.name}</strong> on{" "}
                    {photoCount} photos
                  </>
                ) : (
                  "Select a template to continue"
                )}
              </span>
              <div className="st-footer__actions">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="st-footer__cancel"
                >
                  Cancel
                </button>
                <button
                  disabled={(!landcsapeTemp && !portraitTemp) || applying}
                  onClick={handleTemplateNext}
                  className="st-footer__next"
                >
                  {applying ? (
                    "Applying…"
                  ) : (
                    <>
                      Apply to {photoCount} Photos <span aria-hidden="true">&rarr;</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>
      <PremiumGateDialog
        open={showGateDialog}
        onClose={() => setShowGateDialog(false)}
        title="Premium Templates"
        message="Upgrade to Pro to access all templates. Free users can only use the first template."
      />
    </>
  );
};

export default SelectTemplate;
